/**
 * Battle Born Bodies — Global Theme Scripts
 * 
 * Includes:
 * - Event Bus
 * - Cart API Helpers
 * - Utility Functions (debounce, formatMoney, focus trapping)
 * - Section Event Listeners
 */

'use strict';

(function () {
    /**
     * ==============================================================================
     * EVENT BUS
     * ==============================================================================
     */

    /**
     * Dispatches a custom event on the document.
     * @param {string} eventName - The name of the event to publish.
     * @param {Object} [data={}] - The detailed data to pass with the event.
     */
    function publish(eventName, data = {}) {
        const event = new CustomEvent(eventName, { detail: data });
        document.dispatchEvent(event);
    }

    /**
     * Subscribes to a custom event on the document.
     * @param {string} eventName - The name of the event to listen for.
     * @param {Function} callback - The callback function to execute.
     */
    function subscribe(eventName, callback) {
        document.addEventListener(eventName, callback);
    }

    /**
     * ==============================================================================
     * CART API HELPERS
     * ==============================================================================
     */

    /**
     * Adds an item to the cart via the AJAX API.
     * @param {number|string} id - The variant ID to add.
     * @param {number} [quantity=1] - The quantity to add.
     * @returns {Promise<Object>} The updated cart object.
     */
    function addToCart(id, quantity = 1) {
        const body = JSON.stringify({
            items: [{ id: id, quantity: quantity }]
        });

        return fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: body
        })
            .then((response) => response.json())
            .then((response) => {
                if (response.status) {
                    throw new Error(response.description);
                }
                publish('cart:item-added', { cart: response });
                // Fetch full cart to update UI globally
                return getCart();
            })
            .then((cart) => {
                publish('cart:updated', { cart: cart });
                return cart;
            })
            .catch((error) => {
                console.error('Error adding to cart:', error);
                throw error;
            });
    }

    /**
     * Updates cart line items via the AJAX API.
     * @param {Object} updates - An object where keys are variant IDs and values are quantities.
     * @returns {Promise<Object>} The updated cart object.
     */
    function updateCart(updates) {
        return fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: updates })
        })
            .then((response) => response.json())
            .then((cart) => {
                if (cart.status) {
                    throw new Error(cart.description);
                }
                publish('cart:updated', { cart: cart });
                return cart;
            })
            .catch((error) => {
                console.error('Error updating cart:', error);
                throw error;
            });
    }

    /**
     * Fetches the current cart state.
     * @returns {Promise<Object>} The current cart object.
     */
    function getCart() {
        return fetch(window.Shopify.routes.root + 'cart.js')
            .then((response) => response.json())
            .then((cart) => {
                return cart;
            })
            .catch((error) => {
                console.error('Error fetching cart:', error);
                throw error;
            });
    }

    /**
     * ==============================================================================
     * UTILITY FUNCTIONS
     * ==============================================================================
     */

    /**
     * Debounces a function execution.
     * @param {Function} fn - The function to debounce.
     * @param {number} wait - The delay in milliseconds.
     * @returns {Function} The debounced function.
     */
    function debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }

    /**
     * Formats a price in cents to currency string.
     * @param {number|string} cents - The price in cents.
     * @param {string} [format] - Optional format string (defaults to theme setting if available).
     * @returns {string} The formatted price.
     */
    function formatMoney(cents, format) {
        if (typeof cents === 'string') {
            cents = cents.replace('.', '');
        }
        let value = '';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || window.theme.moneyFormat || '${{amount}}';

        function defaultOption(opt, def) {
            return typeof opt == 'undefined' ? def : opt;
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
            precision = defaultOption(precision, 2);
            thousands = defaultOption(thousands, ',');
            decimal = defaultOption(decimal, '.');

            if (isNaN(number) || number == null) {
                return 0;
            }

            number = (number / 100.0).toFixed(precision);

            const parts = number.split('.');
            const dollars = parts[0].replace(
                /(\d)(?=(\d{3})+(?!\d))/g,
                '$1' + thousands
            );
            const cents = parts[1] ? decimal + parts[1] : '';

            return dollars + cents;
        }

        switch (formatString.match(placeholderRegex)[1]) {
            case 'amount':
                value = formatWithDelimiters(cents, 2);
                break;
            case 'amount_no_decimals':
                value = formatWithDelimiters(cents, 0);
                break;
            case 'amount_with_comma_separator':
                value = formatWithDelimiters(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = formatWithDelimiters(cents, 0, '.', ',');
                break;
        }

        return formatString.replace(placeholderRegex, value);
    }

    /**
     * Traps focus within a specific element for accessibility.
     * @param {HTMLElement} element - The element to trap focus within.
     */
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function trapFocus(element) {
        const focusableElements = Array.from(element.querySelectorAll(focusableSelectors));
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Create a listener specific to this trapping session
        function handleTrap(e) {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) { // Shift + Tab
                if (document.activeElement === firstElement) {
                    lastElement.focus();
                    e.preventDefault();
                }
            } else { // Tab
                if (document.activeElement === lastElement) {
                    firstElement.focus();
                    e.preventDefault();
                }
            }
        }

        element.addEventListener('keydown', handleTrap);

        // Store cleanup function on the element for removal
        element._trapFocusCleanup = () => {
            element.removeEventListener('keydown', handleTrap);
            delete element._trapFocusCleanup;
        };

        // Focus the first element initially
        if (firstElement) {
            firstElement.focus();
        }
    }

    /**
     * Removes the focus trap from an element.
     * @param {HTMLElement} element - The element to remove trap from.
     */
    function removeTrapFocus(element = document.body) {
        if (element && element._trapFocusCleanup) {
            element._trapFocusCleanup();
        }
        // Return focus to the trigger element if stored
        if (window.lastFocusedElement) {
            window.lastFocusedElement.focus();
        }
    }

    /**
     * Pauses all media elements (video, audio) on the page.
     */
    function pauseAllMedia() {
        document.querySelectorAll('.js-youtube').forEach((video) => {
            video.contentWindow.postMessage(
                '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
                '*'
            );
        });
        document.querySelectorAll('.js-vimeo').forEach((video) => {
            video.contentWindow.postMessage('{"method":"pause"}', '*');
        });
        document.querySelectorAll('video').forEach((video) => video.pause());
        document.querySelectorAll('audio').forEach((audio) => audio.pause());
    }

    /**
     * ==============================================================================
     * GLOBAL EXPORTS
     * ==============================================================================
     */

    window.theme = window.theme || {};

    // Expose utilities to global theme object
    window.theme.utils = {
        debounce,
        formatMoney,
        trapFocus,
        removeTrapFocus,
        pauseAllMedia
    };

    // Expose Cart API
    window.theme.cart = {
        add: addToCart,
        update: updateCart,
        get: getCart
    };

    // Expose Event Bus
    // Expose Event Bus
    window.theme.pubsub = {
        publish,
        subscribe
    };

    /**
     * ==============================================================================
     * DELIVERY DISTANCE & SHIPPING RULES
     * ==============================================================================
     */
     
    function haversineDistance(lat1, lon1, lat2, lon2) {
        const R = 3958.8; // Radius of Earth in Miles
        const rLat1 = lat1 * (Math.PI / 180);
        const rLat2 = lat2 * (Math.PI / 180);
        const dLat = (lat2 - lat1) * (Math.PI / 180);
        const dLon = (lon2 - lon1) * (Math.PI / 180);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(rLat1) * Math.cos(rLat2) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return parseFloat((R * c).toFixed(1));
    }

    window.theme.lockCheckoutGlobal = function() {
        document.querySelectorAll('[name="checkout"]').forEach(btn => {
            if (!btn.hasAttribute('data-vitamin-locked')) {
                btn.disabled = true;
                btn.style.opacity = '0.5';
                btn.style.cursor = 'not-allowed';
            }
        });
    };

    window.theme.unlockCheckoutGlobal = function() {
        document.querySelectorAll('[name="checkout"]').forEach(btn => {
            if (!btn.hasAttribute('data-vitamin-locked')) {
                btn.disabled = false;
                btn.style.opacity = '1';
                btn.style.cursor = 'pointer';
            }
        });
    };

    window.theme.evaluateDeliveryZip = function(zipCode, isDrawer = true) {
        const statusEl = document.getElementById(isDrawer ? 'distance-status-msg' : 'distance-status-msg-page');
        const calculateBtn = document.getElementById(isDrawer ? 'calculate-distance-btn' : 'calculate-distance-btn-page');
        
        if (!zipCode || zipCode.length < 5) return;

        // Visual loading
        if (calculateBtn) calculateBtn.innerHTML = '...';

        // 1. Fetch Lat/Long for User's ZIP
        fetch(`https://api.zippopotam.us/us/${zipCode}`)
            .then(res => {
                if (!res.ok) throw new Error('Zip code not found or API down');
                return res.json();
            })
            .then(data => {
                if(calculateBtn) calculateBtn.innerHTML = 'Verify';
                if (!data.places || data.places.length === 0) throw new Error('Invalid Zip');
                
                const userLat = parseFloat(data.places[0].latitude);
                const userLon = parseFloat(data.places[0].longitude);
                
                // Store Origin (2701 SW 105th Ave, Miami FL 33165)
                const originLat = 25.7486;
                const originLon = -80.3664;

                const miles = haversineDistance(originLat, originLon, userLat, userLon);
                
                // 2. Determine Zone
                let zone = 0; let minOrder = 0; let freeThreshold = 0; let shippingCost = 0;

                if (miles <= 5) {
                    zone = 1; minOrder = 50; freeThreshold = 100; shippingCost = 3;
                } else if (miles <= 12) {
                    zone = 2; minOrder = 60; freeThreshold = 110; shippingCost = 6;
                } else if (miles <= 20) {
                    zone = 3; minOrder = 60; freeThreshold = 120; shippingCost = 9;
                } else if (miles <= 30) {
                    zone = 4; minOrder = 80; freeThreshold = 140; shippingCost = 10;
                }

                // 3. Evaluate dynamically
                return fetch(window.Shopify.routes.root + 'cart.js')
                    .then(r => r.json())
                    .then(cart => {
                        window.theme.handleZoneLogic(zone, miles, minOrder, freeThreshold, shippingCost, cart.total_price / 100, isDrawer);
                    });
            })
            .catch(err => {
                console.warn("Geocoding API fail:", err);
                if(calculateBtn) calculateBtn.innerHTML = 'Verify';
                
                // Fail-safe
                if(statusEl) {
                    statusEl.style.display = 'block';
                    statusEl.style.backgroundColor = 'rgba(255, 152, 0, 0.1)';
                    statusEl.style.color = '#e65100';
                    statusEl.innerHTML = "Proceed to checkout to calculate final delivery rates.";
                }
                window.theme.unlockCheckoutGlobal();
            });
    };

    window.theme.handleZoneLogic = function(zone, miles, minOrder, freeThreshold, shippingCost, cartTotal, isDrawer) {
        const statusEl = document.getElementById(isDrawer ? 'distance-status-msg' : 'distance-status-msg-page');
        if (!statusEl) return;
        statusEl.style.display = 'block';

        // Hide input wrapper and update label with Edit button
        const zipWrapper = document.getElementById(isDrawer ? 'cart-zip-input-wrapper' : 'cart-zip-input-wrapper-page');
        const zipLabel = document.getElementById(isDrawer ? 'cart-zip-label' : 'cart-zip-label-page');
        if (zipWrapper && zipLabel) {
            zipWrapper.style.display = 'none';
            const savedZip = localStorage.getItem('deliveryZip');
            zipLabel.innerHTML = `<span style="text-transform: none;">📍 ${savedZip}</span> <button type="button" onclick="window.theme.editZip(${isDrawer})" style="background:none;border:none;color:var(--color-primary, #ff3b30);text-decoration:underline;cursor:pointer;font-size:11px;padding:0 5px;font-family:inherit;">(Edit)</button>`;
        }

        if (zone === 0) {
            statusEl.style.backgroundColor = 'rgba(255, 59, 48, 0.1)';
            statusEl.style.color = '#ff3b30';
            statusEl.innerHTML = `You are ${miles} miles away. We only deliver within 30 miles.`;
            window.theme.lockCheckoutGlobal();
            return;
        }

        if (cartTotal < minOrder) {
            statusEl.style.backgroundColor = 'rgba(255, 59, 48, 0.1)';
            statusEl.style.color = '#ff3b30';
            const diff = (minOrder - cartTotal).toFixed(2);
            statusEl.innerHTML = `Zone ${zone}: $${minOrder} Minimum required. Add $${diff} to unlock delivery.`;
            window.theme.lockCheckoutGlobal();
        } else if (cartTotal < freeThreshold) {
            statusEl.style.backgroundColor = 'rgba(255, 152, 0, 0.1)';
            statusEl.style.color = '#e65100';
            const diff = (freeThreshold - cartTotal).toFixed(2);
            statusEl.innerHTML = `Zone ${zone}: Minimum met ($${shippingCost} delivery). Add $${diff} for FREE delivery!`;
            window.theme.unlockCheckoutGlobal();
        } else {
            statusEl.style.backgroundColor = 'rgba(76, 175, 80, 0.1)';
            statusEl.style.color = '#4CAF50';
            statusEl.innerHTML = `Zone ${zone}: You've unlocked FREE delivery!`;
            window.theme.unlockCheckoutGlobal();
        }
    };

    window.theme.editZip = function(isDrawer) {
        const zipWrapper = document.getElementById(isDrawer ? 'cart-zip-input-wrapper' : 'cart-zip-input-wrapper-page');
        const zipLabel = document.getElementById(isDrawer ? 'cart-zip-label' : 'cart-zip-label-page');
        const statusEl = document.getElementById(isDrawer ? 'distance-status-msg' : 'distance-status-msg-page');
        
        if (zipWrapper && zipLabel) {
            zipWrapper.style.display = 'flex';
            zipLabel.innerHTML = 'Delivery Zip Code';
            if (isDrawer) {
                document.getElementById('delivery-zip-input').focus();
            } else {
                document.getElementById('delivery-zip-input-page').focus();
            }
        }
        if (statusEl) {
            statusEl.style.display = 'none';
        }
        window.theme.lockCheckoutGlobal(); // Lock until they verify again
    };

    /**
     * ==============================================================================
     * DELIVERY DAY RULES
     * Meals go out on Wednesdays and Sundays. A day can be picked when it falls on
     * or after the New York calendar date two days from now (48hr prep window).
     * Each meal line stores a readable "Delivery Day" for the kitchen plus a hidden
     * "_delivery_date" (YYYY-MM-DD) so a saved cart can be re-checked later.
     * ==============================================================================
     */

    const DELIVERY_WEEKDAYS = [3, 0]; // Wednesday, Sunday (also the display order)
    const DAY_MS = 24 * 60 * 60 * 1000;
    const MONTH_ABBREVIATIONS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const deliveryLabelFormat = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric'
    });
    const newYorkDateFormat = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric'
    });

    /**
     * Today's calendar date in New York, as a UTC-midnight Date (no time-of-day).
     * @param {Date} [now]
     * @returns {Date}
     */
    function getNewYorkToday(now = new Date()) {
        const parts = {};
        newYorkDateFormat.formatToParts(now).forEach((part) => {
            parts[part.type] = part.value;
        });
        return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    }

    function toIsoDate(date) {
        return date.toISOString().slice(0, 10);
    }

    /**
     * The delivery days a customer can pick right now, Wednesday first.
     * @param {Date} [now]
     * @returns {Array<{iso: string, label: string, earliest: boolean}>}
     */
    function getDeliveryOptions(now) {
        const firstAllowed = new Date(getNewYorkToday(now).getTime() + 2 * DAY_MS);
        const options = DELIVERY_WEEKDAYS.map((weekday) => {
            const daysAhead = (weekday - firstAllowed.getUTCDay() + 7) % 7;
            const date = new Date(firstAllowed.getTime() + daysAhead * DAY_MS);
            return { iso: toIsoDate(date), label: deliveryLabelFormat.format(date) };
        });
        const earliestIso = options.reduce((min, option) => (option.iso < min ? option.iso : min), options[0].iso);
        options.forEach((option) => {
            option.earliest = option.iso === earliestIso;
        });
        return options;
    }

    function getEarliestDeliveryOption(now) {
        return getDeliveryOptions(now).find((option) => option.earliest);
    }

    /**
     * Reads a year-less label like "Wednesday, Oct 14" (carts saved before
     * "_delivery_date" existed) and picks the year closest to today.
     * @returns {string|null} YYYY-MM-DD
     */
    function parseDeliveryLabel(label, now) {
        const match = /([A-Za-z]{3})[A-Za-z]*\.?\s+(\d{1,2})\b/.exec(String(label || '').split(',').pop());
        if (!match) return null;
        const month = MONTH_ABBREVIATIONS.indexOf(match[1].toLowerCase());
        if (month === -1) return null;

        const today = getNewYorkToday(now);
        let date = new Date(Date.UTC(today.getUTCFullYear(), month, Number(match[2])));
        if (date - today > 180 * DAY_MS) date = new Date(Date.UTC(today.getUTCFullYear() - 1, month, Number(match[2])));
        if (today - date > 180 * DAY_MS) date = new Date(Date.UTC(today.getUTCFullYear() + 1, month, Number(match[2])));
        return toIsoDate(date);
    }

    /**
     * @param {Object|null} properties - Line item properties from /cart.js
     * @returns {string|null} The line's delivery date as YYYY-MM-DD, if it has one.
     */
    function getLineDeliveryIso(properties, now) {
        if (!properties) return null;
        const iso = properties._delivery_date;
        if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
        return parseDeliveryLabel(properties['Delivery Day'], now);
    }

    /** Meals and desserts need a delivery day; supplements don't. */
    function lineNeedsDeliveryDay(item) {
        if (item.gift_card) return false;
        return !String(item.product_type || '').toLowerCase().includes('vitamin');
    }

    let deliveryRepairInFlight = null;

    /**
     * Silently moves any meal line whose delivery day is missing or no longer
     * available to the earliest available day. Lines are fixed one at a time from
     * the latest cart, so changed keys (or Shopify merging identical lines) are safe.
     * @returns {Promise<{cart: Object, changed: boolean}>}
     */
    function repairCartDeliveryDays() {
        if (deliveryRepairInFlight) return deliveryRepairInFlight;

        deliveryRepairInFlight = (async () => {
            let cart = await getCart();
            let changed = false;
            const earliest = getEarliestDeliveryOption();
            const maxFixes = cart.items.length;

            for (let fixes = 0; fixes < maxFixes; fixes++) {
                const item = cart.items.find((line) => {
                    if (!lineNeedsDeliveryDay(line)) return false;
                    const iso = getLineDeliveryIso(line.properties);
                    return !iso || iso < earliest.iso;
                });
                if (!item) break;

                const properties = Object.assign({}, item.properties, {
                    'Delivery Day': earliest.label,
                    _delivery_date: earliest.iso
                });
                const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: properties })
                });
                const result = await response.json();
                if (!response.ok || result.status) {
                    throw new Error(result.description || 'Could not update delivery day');
                }
                cart = result;
                changed = true;
            }

            if (changed) publish('cart:updated', { cart: cart });
            return { cart: cart, changed: changed };
        })().finally(() => {
            deliveryRepairInFlight = null;
        });

        return deliveryRepairInFlight;
    }

    window.theme.delivery = {
        getOptions: getDeliveryOptions,
        getEarliest: getEarliestDeliveryOption,
        getLineIso: getLineDeliveryIso,
        lineNeedsDay: lineNeedsDeliveryDay,
        repairCart: repairCartDeliveryDays
    };

    window.theme.initZipDistanceCheck = function() {
        const saved = localStorage.getItem('deliveryZip');
        
        // Drawer elements
        const zipDrawer = document.getElementById('delivery-zip-input');
        const btnDrawer = document.getElementById('calculate-distance-btn');
        if (zipDrawer && btnDrawer) {
            if(saved) zipDrawer.value = saved;
            btnDrawer.onclick = () => {
                localStorage.setItem('deliveryZip', zipDrawer.value);
                window.theme.evaluateDeliveryZip(zipDrawer.value, true);
            };
            if(saved) window.theme.evaluateDeliveryZip(saved, true);
            else window.theme.lockCheckoutGlobal();
        }

        // Page elements
        const zipPage = document.getElementById('delivery-zip-input-page');
        const btnPage = document.getElementById('calculate-distance-btn-page');
        if (zipPage && btnPage) {
            if(saved) zipPage.value = saved;
            btnPage.onclick = () => {
                localStorage.setItem('deliveryZip', zipPage.value);
                window.theme.evaluateDeliveryZip(zipPage.value, false);
            };
            if(saved) window.theme.evaluateDeliveryZip(saved, false);
            else window.theme.lockCheckoutGlobal();
        }
    };

    /**
     * ==============================================================================
     * SECTION EVENTS
     * ==============================================================================
     */

    document.addEventListener('shopify:section:load', (e) => {
        // Re-initialize dynamic components in the loaded section
        console.log('Section loaded:', e.target);
    });

    document.addEventListener('shopify:section:unload', (e) => {
        // Clean up event listeners or observers
        console.log('Section unloaded:', e.target);
    });

    document.addEventListener('shopify:block:select', (e) => {
        // Scroll to selected block
        const block = e.target;
        if (block) {
            block.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    });

    document.addEventListener('shopify:block:deselect', (e) => {
        // Handle block deselection if necessary
    });

    // Initialize common UI elements on DOMContentLoaded
    document.addEventListener('DOMContentLoaded', () => {
        // Example: Initialize smooth scrolling for anchor links
        document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
            anchor.addEventListener('click', function (e) {
                e.preventDefault();
                const targetId = this.getAttribute('href').substring(1);
                const target = document.getElementById(targetId);
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth'
                    });
                }
            });
        });
        
        // Initialize cart page distance module
        if (window.theme && typeof window.theme.initZipDistanceCheck === 'function') {
            window.theme.initZipDistanceCheck();
        }
    });

})();
